import { useContext, useEffect } from 'react'
import { withRouter } from 'react-router-dom'
import * as H from 'history'
import { ContentBody, ContentHeader } from 'components/shared/content'
import { CopyTableButton, ExTable, TableBody, TableHead, TableWrapper } from 'components/shared/widget'
import { useFetchPcrProcessLogsApi } from 'api/pcr_process_log'
import { PcrProcessLog, PcrProcessLogSearchForm } from 'entities/pcr_process_log'
import { Pagination } from 'components/shared/pagination'
import { useForm, useQuery } from 'utils/hooks'
import { SearchConditionView } from 'components/shared/search_drawer'
import { GlobalStateContext } from 'contexts/global_state_context'
import { pcrProcessLogColumnInfoList, PcrProcessLogTableData } from './pcr_process_log_table'
import { DisplaySetting, DisplaySettingModal, TableHeads, useDisplaySetting } from 'components/shared/display_setting'
import qs from 'qs'
import { Flex } from 'components/shared/flex'
import { sampleColumnInfoList, SampleTableData } from 'components/sample/sample_table'
import { extractionColumnInfoList, ExtractionTableData } from 'components/extraction/extraction_table'
import { dnaColumnInfoList, DnaTableData } from 'components/dna/dna_table'
import { pcrRunColumnInfoList, PcrRunTableData } from 'components/pcr_run/pcr_run_table'

type Props = {
    history: H.History
}

const PcrProcessLogsPage = (props: Props) => {
    const globalState = useContext(GlobalStateContext)
    const query = useQuery<PcrProcessLogSearchForm>()
    const searchForm = useForm<PcrProcessLogSearchForm>(query)
    const api = useFetchPcrProcessLogsApi(searchForm)
    const sampleDisplaySetting = useDisplaySetting(sampleColumnInfoList(searchForm), 'PcrProcessLog', 'default_hidden')
    const extractionDisplaySetting = useDisplaySetting(extractionColumnInfoList(searchForm), 'PcrProcessLog', 'default_hidden')
    const dnaDisplaySetting = useDisplaySetting(dnaColumnInfoList(searchForm), 'PcrProcessLog', 'default_hidden')
    const pcrRunDisplaySetting = useDisplaySetting(pcrRunColumnInfoList(searchForm), 'PcrProcessLog', 'default_hidden')
    const pcrProcessLogDisplaySetting = useDisplaySetting(pcrProcessLogColumnInfoList(searchForm), 'PcrProcessLog')
    const displaySettings: DisplaySetting[] = [
        sampleDisplaySetting,
        extractionDisplaySetting,
        dnaDisplaySetting,
        pcrRunDisplaySetting,
        pcrProcessLogDisplaySetting,
    ]

    useEffect(() => {
        const queryParams = qs.stringify({ ...searchForm.object, page: api.pageSet.page, perPage: api.pageSet.perPage })
        if (props.history.location.search.replace('?', '') !== queryParams) {
            props.history.replace(`pcr_process_logs?${queryParams}`)
        }
    }, [searchForm.object])

    useEffect(() => {
        api.execute()
    }, [])

    const handleClickRemoveFilter = (key: keyof PcrProcessLogSearchForm): void => {
        searchForm.update((f) => {
            delete f[key]
        })
    }

    return (
        <>
            <ContentHeader
                headerTitle="PcrProcessLog"
                left={
                    <>
                        <CopyTableButton />
                        <SearchConditionView searchForm={searchForm} onClickRemoveButton={handleClickRemoveFilter} />
                    </>
                }
                right={<DisplaySettingModal displaySettings={displaySettings} />}
            />
            <Flex style={{ marginTop: 5 }} alignItems="center" justifyContent="space-between">
                <Flex alignItems="center">
                    <Pagination pageSet={api.pageSet} response={api.response}></Pagination>
                    <div style={{ minWidth: 140, textAlign: 'right' }}>総件数： {api.response.count.toLocaleString()}件</div>
                </Flex>
            </Flex>
            <ContentBody>
                <TableWrapper contentHeaderCount={2}>
                    <ExTable className="sticky-table">
                        <TableHead>
                            <tr>
                                {displaySettings.map((displaySetting) => (
                                    <TableHeads key={displaySetting.columnInfoList.tableName} displaySetting={displaySetting} />
                                ))}
                            </tr>
                        </TableHead>
                        <TableBody
                            loading={api.loading}
                            columnLength={
                                sampleDisplaySetting.getColumnLength() +
                                extractionDisplaySetting.getColumnLength() +
                                dnaDisplaySetting.getColumnLength() +
                                pcrProcessLogDisplaySetting.getColumnLength() +
                                pcrRunDisplaySetting.getColumnLength()
                            }
                        >
                            {api.response.results.map((pcrProcessLog: PcrProcessLog) => {
                                return (
                                    <tr key={pcrProcessLog.id} style={{ backgroundColor: pcrProcessLog.result ? '#0801' : '#8001' }}>
                                        <SampleTableData sample={pcrProcessLog.dna?.extraction?.sample} displaySetting={sampleDisplaySetting} />
                                        <ExtractionTableData extraction={pcrProcessLog.dna?.extraction} displaySetting={extractionDisplaySetting} />
                                        <DnaTableData dna={pcrProcessLog.dna} displaySetting={dnaDisplaySetting} />
                                        <PcrRunTableData onClickDetail={() => {}} pcrRun={pcrProcessLog.pcrRun} displaySetting={pcrRunDisplaySetting} />
                                        <PcrProcessLogTableData pcrProcessLog={pcrProcessLog} displaySetting={pcrProcessLogDisplaySetting} />
                                    </tr>
                                )
                            })}
                        </TableBody>
                    </ExTable>
                    <Pagination pageSet={api.pageSet} response={api.response}></Pagination>
                </TableWrapper>
            </ContentBody>
        </>
    )
}

export default withRouter(PcrProcessLogsPage)
